import React, { useState, useMemo, useCallback } from "react";
import {
  Chip,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { sendMessageToAgent } from "../../../store/userSlice";
import { v4 as uuid } from "uuid";
import styles from "../agent-response.module.scss";

type Skill = {
  skill_name: string;
  skill_description: string;
  proficiency_level: number;
  proficiency: string;
  is_recommended: boolean;
  skill_description_type?: string;
};

type SkillType = {
  skill_type: string;
  skills: Skill[];
};

type RoleData = {
  role: string;
  role_type: string;
  organization_level_role?: string;
  type_of_skills: SkillType[];
};

type SkillIntelligenceAgentProps = {
  parsedContent: any;
};

export default function SkillIntelligenceAgent({
  parsedContent,
}: SkillIntelligenceAgentProps): JSX.Element {
  const dispatch = useAppDispatch();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [clickedButton, setClickedButton] = useState<string | null>(null);

  // Get current agent, loading state, workflow_id, and request_id
  const currentAgent = useSelector(
    (state: RootState) => state.agents?.currentAgent,
  );
  const loading = useSelector((state: RootState) => state.user.loading);
  const workflowId = useSelector((state: RootState) => state.user.workflowId);
  const requestId = useSelector((state: RootState) => state.user.requestId);

  // Parse the content
  const roleData = useMemo<RoleData | null>(() => {
    try {
      if (parsedContent?.finalAnswer && Array.isArray(parsedContent.finalAnswer)) {
        const finalAnswerStr = parsedContent.finalAnswer[0];
        if (typeof finalAnswerStr === "string") {
          return JSON.parse(finalAnswerStr);
        } else {
          return finalAnswerStr;
        }
      } else if (Array.isArray(parsedContent)) {
        return parsedContent[0];
      } else if (parsedContent?.answer) {
        return Array.isArray(parsedContent.answer)
          ? parsedContent.answer[0]
          : parsedContent.answer;
      } else {
        return parsedContent;
      }
    } catch (error) {
      console.error("Error parsing Skill Intelligence data:", error);
      return null;
    }
  }, [parsedContent]);

  // Sort skills by proficiency_level in descending order
  const sortedRoleData = useMemo<RoleData | null>(() => {
    if (!roleData) return null;

    return {
      ...roleData,
      type_of_skills: roleData.type_of_skills.map((skillTypeGroup) => ({
        ...skillTypeGroup,
        skills: [...skillTypeGroup.skills].sort(
          (a, b) => b.proficiency_level - a.proficiency_level
        ),
      })),
    };
  }, [roleData]);

  if (!sortedRoleData) {
    return (
      <div className={styles.pressReleaseContent}>
        <p>No role data available.</p>
      </div>
    );
  }

  const toggleSection = (skillType: string) => {
    setExpandedSections((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(skillType)) {
        newExpanded.delete(skillType);
      } else {
        newExpanded.add(skillType);
      }
      return newExpanded;
    });
  };

  const getProficiencyBadgeStyle = (level: number) => {
    if (level >= 5) {
      return {
        color: "#267103",
        backgroundColor: "#dcfada",
        border: "1px solid #39a9064d",
      };
    }
    if (level >= 4) {
      return {
        color: "#905d13",
        backgroundColor: "#faedda",
        border: "1px solid #f5d9a3",
      };
    }
    if (level >= 3) {
      return {
        color: "#5b6b7a",
        backgroundColor: "#e8eaed",
        border: "1px solid #c5cad1",
      };
    }
    return {
      color: "#bd1919",
      backgroundColor: "#fadada",
      border: "1px solid #fadada4d",
    };
  };

  const getProficiencyColor = (level: number): string => {
    if (level >= 5) return "#267103";
    if (level >= 4) return "#905d13";
    if (level >= 3) return "#5b6b7a";
    return "#bd1919";
  };

  // Memoize question extraction
  const question = useMemo(() => parsedContent?.question || "", [parsedContent]);

  // Handle action button clicks
  const handleYesProceed = useCallback(() => {
    if (clickedButton) return;

    setClickedButton("yes");

    const params = new URLSearchParams(location.search);
    const sessionId = params.get("sessionId");

    dispatch(
      sendMessageToAgent({
        agentId: currentAgent?.id || 40,
        message: "Yes, proceed",
        questionId: uuid(),
        timestamp: new Date().toISOString(),
        agentEndpoint: currentAgent?.endpoint || "/stream/skill-intelligence",
        sessionId,
        modify_array: [],
      }),
    );
  }, [clickedButton, currentAgent, dispatch]);

  const handleModifyRequest = useCallback(() => {
    if (clickedButton) return;

    setClickedButton("modify");

    const params = new URLSearchParams(location.search);
    const sessionId = params.get("sessionId");

    dispatch(
      sendMessageToAgent({
        agentId: currentAgent?.id || 40,
        message: "I'll modify",
        questionId: uuid(),
        timestamp: new Date().toISOString(),
        agentEndpoint: currentAgent?.endpoint || "/stream/skill-intelligence",
        sessionId,
        modify_array: [],
      }),
    );
  }, [clickedButton, currentAgent, dispatch]);

  return (
    <div className={styles.pressReleaseContent}>
      {/* Role Header */}
      <div style={{ marginBottom: "24px" }}>
        <h2 style={{ marginBottom: "8px", color: "#004141" }}>
          {sortedRoleData.role}
        </h2>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <div
            style={{
              display: "inline-block",
              padding: "4px 12px",
              backgroundColor: "#00d2aa15",
              border: "1px solid #00d2aa33",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#00a888",
            }}
          >
            {sortedRoleData.role_type}
          </div>
          {sortedRoleData.organization_level_role && (
            <div
              style={{
                display: "inline-block",
                padding: "4px 12px",
                backgroundColor: "#f0f4ff",
                border: "1px solid #d0e0ff",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#4a6fa5",
              }}
            >
              {sortedRoleData.organization_level_role}
            </div>
          )}
        </Box>
      </div>

      {/* Skills and Proficiency Mapping */}
      <Box sx={{ mt: 4 }}>
        <h3
          style={{
            margin: "0 0 20px 0",
            fontSize: "18px",
            fontWeight: "600",
            color: "#004141",
            borderBottom: "2px solid #00a888",
            paddingBottom: "8px",
          }}
        >
          Skills and Proficiency Mapping
        </h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {sortedRoleData.type_of_skills.map((skillTypeGroup, typeIndex) => {
            const skillTypeCount = skillTypeGroup.skills.filter(
              (s) => s.skill_description_type
            ).length;

            return (
              <Box key={typeIndex}>
                {/* Skill Type Header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1.5,
                    cursor: "pointer",
                    "&:hover": {
                      "& h4": {
                        color: "#00a888",
                      },
                    },
                  }}
                  onClick={() => toggleSection(skillTypeGroup.skill_type)}
                >
                  <IconButton
                    size="small"
                    sx={{
                      color: "#004141",
                      padding: 0,
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: "#00a888",
                      },
                    }}
                  >
                    {expandedSections.has(skillTypeGroup.skill_type) ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                  <h4
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#004141",
                      transition: "color 0.2s ease",
                    }}
                  >
                    {skillTypeGroup.skill_type}
                  </h4>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      backgroundColor: "#f5f5f5",
                      padding: "2px 8px",
                      borderRadius: "4px",
                    }}
                  >
                    {skillTypeGroup.skills.length} skills
                    {skillTypeCount > 0 &&
                      ` • ${skillTypeCount} ${skillTypeGroup.skills[0]?.skill_description_type || ""}`}
                  </div>
                </Box>

                {/* Skills Table */}
                {expandedSections.has(skillTypeGroup.skill_type) && (
                  <TableContainer
                    component={Paper}
                    sx={{
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#004141",
                              fontSize: "13px",
                              width: "20%",
                              borderBottom: "2px solid #e0e0e0",
                            }}
                          >
                            Skill Name
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#004141",
                              fontSize: "13px",
                              width: "55%",
                              borderBottom: "2px solid #e0e0e0",
                            }}
                          >
                            Definition
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#004141",
                              fontSize: "13px",
                              width: "15%",
                              borderBottom: "2px solid #e0e0e0",
                            }}
                          >
                            Proficiency
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#004141",
                              fontSize: "13px",
                              width: "10%",
                              textAlign: "center",
                              borderBottom: "2px solid #e0e0e0",
                            }}
                          >
                            Level
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {skillTypeGroup.skills.map((skill, skillIndex) => (
                          <TableRow
                            key={skillIndex}
                            sx={{
                              "&:hover": {
                                backgroundColor: "#fafafa",
                              },
                              borderLeft: skill.skill_description_type
                                ? "3px solid #00d2aa33"
                                : "none",
                            }}
                          >
                            <TableCell
                              sx={{
                                fontSize: "14px",
                                color: "#004141",
                                fontWeight: 500,
                                verticalAlign: "top",
                                py: 2,
                              }}
                            >
                              <Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                    mb: skill.skill_description_type ? 0.5 : 0,
                                  }}
                                >
                                  <span style={{ lineHeight: "1.4" }}>
                                    {skillIndex + 1}. {skill.skill_name}
                                  </span>
                                </Box>
                                {skill.skill_description_type && (
                                  <Box
                                    component="span"
                                    sx={{
                                      fontSize: "9px",
                                      fontWeight: 600,
                                      color: "#00a888",
                                      backgroundColor: "#dcfaf4",
                                      border: "1px solid #00d2aa33",
                                      borderRadius: "3px",
                                      px: 0.75,
                                      py: 0.375,
                                      lineHeight: 1.2,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.3px",
                                      display: "inline-flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    {skill.skill_description_type}
                                  </Box>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: "13px",
                                color: "#403f3f",
                                lineHeight: 1.6,
                                verticalAlign: "top",
                                py: 2,
                              }}
                            >
                              {skill.skill_description}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: "13px",
                                verticalAlign: "top",
                                py: 2,
                              }}
                            >
                              <Chip
                                label={skill.proficiency}
                                size="small"
                                sx={{
                                  fontSize: "11px",
                                  fontWeight: 500,
                                  ...getProficiencyBadgeStyle(skill.proficiency_level),
                                }}
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                textAlign: "center",
                                color: getProficiencyColor(skill.proficiency_level),
                                verticalAlign: "top",
                                py: 2,
                              }}
                            >
                              {skill.proficiency_level}/5
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            );
          })}
        </div>
      </Box>

      {/* Question - Display if present */}
      {question && <div className={styles.finalQuestion}>{question}</div>}

      {/* Action Buttons - Only show when question exists */}
      {question && (
        <div style={{ display: "flex", gap: "16px", marginTop: "24px", justifyContent: "center" }}>
          <Button
            variant="outlined"
            size="large"
            onClick={handleYesProceed}
            disabled={clickedButton !== null || loading}
            sx={{
              textTransform: "none",
              fontSize: "14px",
              padding: "10px 24px",
              borderColor: "#00a888",
              color: "#00a888",
              "&:hover": {
                borderColor: "#00a888",
                backgroundColor: "#e0f7fa",
              },
              "&.Mui-disabled": {
                opacity: 0.6,
                borderColor: "#d0d0d0",
                backgroundColor: "#f5f5f5",
                color: "#a0a0a0",
                cursor: "not-allowed",
              },
            }}
          >
            Yes, proceed
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={handleModifyRequest}
            disabled={clickedButton !== null || loading}
            sx={{
              textTransform: "none",
              fontSize: "14px",
              padding: "10px 24px",
              borderColor: "#00a888",
              color: "#00a888",
              "&:hover": {
                borderColor: "#00a888",
                backgroundColor: "#e0f7fa",
              },
              "&.Mui-disabled": {
                opacity: 0.6,
                borderColor: "#d0d0d0",
                backgroundColor: "#f5f5f5",
                color: "#a0a0a0",
                cursor: "not-allowed",
              },
            }}
          >
            I'll modify
          </Button>
        </div>
      )}
    </div>
  );
}
